import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FileCopySvg } from "../../svgFiles/FileCopySvg";
import Pagination from "../../components/shared/pagination/Pagination";
import Loader from "../../components/shared/loader/Loader";
import { showToast } from "../../helpers/Utils";
import { Routes as AppRoutes } from "../../routes";

const STATUS_CLASS = {
  redeemed: "redemption-badge--redeemed",
  unused: "redemption-badge--unused",
  expired: "redemption-badge--expired",
};

const STATUS_LABEL = {
  redeemed: "Redeemed",
  unused: "Unused",
  expired: "Expired",
};

const DEFAULT_COLUMN_WIDTHS = {
  code: 30,
  duration: 30,
  status: 15,
  orderId: 12.5,
  action: 12.5,
};

const MIN_COLUMN_WIDTHS = {
  code: 10,
  duration: 10,
  status: 8,
  orderId: 8,
  action: 8,
};

const COLUMN_KEYS = ["code", "duration", "status", "orderId", "action"];

function copyCode(code) {
  if (!code) return;
  navigator.clipboard
    .writeText(code)
    .then(() => showToast("Code copied", "success"))
    .catch(() => showToast("Could not copy code", "error"));
}

function getRedemptionShopifyOrderId(row) {
  const direct =
    row?.orderShopifyId ??
    row?.shopifyOrderId ??
    row?.ShopifyId ??
    row?.shopifyId ??
    "";
  const trimmed = String(direct || "").trim();
  if (trimmed) return trimmed;

  const adminUrl = String(row?.shopifyAdminUrl || "");
  const match = adminUrl.match(/\/orders\/(\d+)/);
  return match?.[1] || "";
}

function getRedemptionOrderDetailsPath(row) {
  const shopifyOrderId = getRedemptionShopifyOrderId(row);
  if (!shopifyOrderId) return null;
  return AppRoutes.OrderDetails.path.replace(":id", shopifyOrderId);
}

function getRedemptionOrderId(row) {
  const orderName =
    row?.shopifyOrderName ?? row?.ShopifyOrderName ?? "";
  const trimmed = String(orderName || "").trim();
  if (trimmed) return trimmed;
  const shortId = String(row?.shortId || "").trim();
  return shortId || "—";
}

export default function RedemptionCodeTable({
  items,
  isLoading,
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
}) {
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);
  const resizeRef = useRef(null);
  const tableRef = useRef(null);

  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  const startResize = useCallback((event, key) => {
    event.preventDefault();
    event.stopPropagation();
    resizeRef.current = {
      key,
      startX: event.clientX,
      startWidth: columnWidths[key] || DEFAULT_COLUMN_WIDTHS[key],
    };
    document.body.classList.add("redemption-col-resizing");
  }, [columnWidths]);

  useEffect(() => {
    const onMove = (event) => {
      const resize = resizeRef.current;
      if (!resize) return;
      const tableW = tableRef.current?.offsetWidth || 1;
      const deltaPct = ((event.clientX - resize.startX) / tableW) * 100;
      const minWidth = MIN_COLUMN_WIDTHS[resize.key] || 5;
      const nextWidth = Math.max(minWidth, resize.startWidth + deltaPct);
      setColumnWidths((prev) => ({ ...prev, [resize.key]: nextWidth }));
    };
    const onUp = () => {
      if (!resizeRef.current) return;
      resizeRef.current = null;
      document.body.classList.remove("redemption-col-resizing");
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.classList.remove("redemption-col-resizing");
    };
  }, []);

  const columns = useMemo(
    () => ({
      code: {
        key: "code",
        header: "Redemption Code",
        cell: (row) => (
          <div className="redemption-code-cell">
            <span className="redemption-code-cell__value">{row.code}</span>
            <button
              type="button"
              className="redemption-code-cell__copy-btn"
              onClick={() => copyCode(row.code)}
              aria-label="Copy redemption code"
            >
              <FileCopySvg className="redemption-code-cell__copy" />
            </button>
          </div>
        ),
      },
      duration: {
        key: "duration",
        header: "Duration",
        cell: (row) => (
          <div className="redemption-duration">
            <strong>{row.durationLabel || "—"}</strong>
            <span>{row.dateRangeLabel || "—"}</span>
          </div>
        ),
      },
      status: {
        key: "status",
        header: "Status",
        cell: (row) => (
          <span className={`redemption-badge ${STATUS_CLASS[row.status] || ""}`}>
            {STATUS_LABEL[row.status] || row.status}
          </span>
        ),
      },
      orderId: {
        key: "orderId",
        header: "Order ID",
        className: "redemption-short-id",
        cell: (row) => getRedemptionOrderId(row),
      },
      action: {
        key: "action",
        header: "",
        className: "text-center",
        cell: (row, orderDetailsPath) =>
          orderDetailsPath ? (
            <Link
              to={orderDetailsPath}
              state={{ fromRedemption: true }}
              className="redemption-shopify-link"
            >
              View Order
            </Link>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
    }),
    []
  );

  const orderedColumns = COLUMN_KEYS.map((key) => columns[key]).filter(Boolean);

  if (isLoading && items.length === 0) {
    return (
      <div className="py-5">
        <Loader />
      </div>
    );
  }

  return (
    <div className="redemption-table-wrap">
      <div className="table-scroll">
        <Table ref={tableRef} className="redemption-table mb-0" style={{ width: "100%" }}>
          <thead>
            <tr>
              {orderedColumns.map((column) => {
                const w = columnWidths[column.key] || DEFAULT_COLUMN_WIDTHS[column.key];
                return (
                  <th
                    key={column.key}
                    className={`${column.className || ""} redemption-table__resizable-head`.trim()}
                    style={{ width: `${w}%` }}
                  >
                    <div className="redemption-table__head-content">
                      <span>{column.header}</span>
                    </div>
                    <span
                      className="redemption-table__resize-handle"
                      onMouseDown={(event) => startResize(event, column.key)}
                      aria-hidden="true"
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={orderedColumns.length} className="text-center text-muted py-5">
                  No redemption codes found
                </td>
              </tr>
            ) : (
              items.map((row) => {
                const id = String(row.id || row._id);
                const orderDetailsPath = getRedemptionOrderDetailsPath(row);
                return (
                  <tr key={id}>
                    {orderedColumns.map((column) => {
                      const w = columnWidths[column.key] || DEFAULT_COLUMN_WIDTHS[column.key];
                      return (
                        <td
                          key={`${id}-${column.key}`}
                          className={column.className || undefined}
                          style={{ width: `${w}%` }}
                        >
                          {column.key === "action"
                            ? column.cell(row, orderDetailsPath)
                            : column.cell(row)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
      <div className="redemption-table__footer">
        <span className="text-muted">
          Showing {start}-{end} of {total}
        </span>
        {totalPages > 1 && (
          <Pagination
            totalPages={totalPages}
            currentPage={page}
            onPageChange={onPageChange}
          />
        )}
      </div>
    </div>
  );
}
